/**
 * renderFooterExtraColumn — maps a serializable, kind-tagged footer
 * extra-column descriptor to its React element (BLOG-STD-001).
 *
 * `MarketingFooter`'s `extraColumns` slot takes React nodes (e.g. protector-web's
 * `<CredentialsFooterColumn>`). The publish-time prerender harness runs in a
 * centralized backend (purchase-api) that cannot import each app's source, so the
 * per-app chrome descriptor carries extra columns as PLAIN DATA
 * (`{ kind: 'credentials', heading, license, ... }`). This shared renderer maps
 * that data → the canonical marketing-shell component, so BOTH the server
 * harness and the client app produce identical footer markup from the same
 * descriptor (hydration-safe, single source of truth for the kind→component map).
 *
 * Adding a new regulated-industry column kind = register one entry here; both
 * the harness and every client consumer pick it up.
 *
 * @module @puffin-core/marketing-shell/renderFooterExtraColumn
 */

import { createElement, cloneElement } from 'react';
import CredentialsFooterColumn from './CredentialsFooterColumn.jsx';
import { FooterLinksColumn } from './FooterLinksColumn.jsx';

const EXTRA_COLUMN_RENDERERS = Object.freeze({
  credentials: (d) =>
    createElement(CredentialsFooterColumn, {
      heading: d.heading,
      license: d.license,
      npn: d.npn,
      drlp: d.drlp,
      eAndO: d.eAndO,
      registry: d.registry,
    }),
  // LIFE-SHORTLISTED-001 Q-LS-32 — a named column of destinations (a tenant site's CMS footer menu,
  // or its site links when `social`), so the tenant site and its server-rendered blog draw one footer.
  links: (d, { onOutboundClick } = {}) =>
    createElement(FooterLinksColumn, {
      heading: d.heading,
      links: d.links,
      social: d.social === true,
      onOutboundClick,
    }),
});

/** Closed set of supported extra-column kinds. */
export const FOOTER_EXTRA_COLUMN_KINDS = Object.freeze(Object.keys(EXTRA_COLUMN_RENDERERS));

/**
 * Render one kind-tagged footer extra-column descriptor → React element. The
 * element may itself render to nothing at React render time (e.g. a credentials
 * column whose data fields are all empty) — that's the component's concern.
 *
 * @param {{kind: string}} descriptor
 * @param {{onOutboundClick?: Function}} [options] - render-time behaviour a descriptor cannot carry
 *   (the client's outbound-click reporter; the server render passes none)
 * @returns {React.ReactElement}
 */
export function renderFooterExtraColumn(descriptor, options) {
  if (!descriptor || typeof descriptor !== 'object' || typeof descriptor.kind !== 'string') {
    throw new TypeError('renderFooterExtraColumn: descriptor with a string `kind` is required');
  }
  const renderer = EXTRA_COLUMN_RENDERERS[descriptor.kind];
  if (!renderer) {
    throw new TypeError(
      `renderFooterExtraColumn: unknown extra-column kind "${descriptor.kind}" (known: ${FOOTER_EXTRA_COLUMN_KINDS.join(', ')})`,
    );
  }
  return renderer(descriptor, options);
}

/**
 * Render an array of kind-tagged descriptors → keyed React elements (the shape
 * `MarketingFooter`'s `extraColumns` prop expects). Each element gets a stable
 * `key` (`descriptor.key ?? `${kind}-${i}``). Elements whose component renders
 * nothing (empty data) stay in the array but produce no DOM.
 *
 * @param {Array<{kind: string, key?: string|number}>} [descriptors]
 * @param {{onOutboundClick?: Function}} [options] - see `renderFooterExtraColumn`
 * @returns {React.ReactElement[]}
 */
export function renderFooterExtraColumns(descriptors, options) {
  if (!Array.isArray(descriptors)) return [];
  return descriptors.map((d, index) =>
    cloneElement(renderFooterExtraColumn(d, options), { key: d.key ?? `${d.kind}-${index}` }),
  );
}
